/**
 * Minimalist SPA routing for tschepp-ar-web — pushState + popstate event,
 * no react-router dep.
 *
 * The app historically navigated via `window.location.href = '/k/...'`
 * which forces a full page reload. That kills the Tschauko voice-session
 * on every page-change, which breaks the "Co-Pilot" UX (bot keeps talking
 * while the page transitions to SpeciesView).
 *
 * This helper gives us client-side navigation without rewriting the whole
 * routing layer: `usePathname()` is a reactive read of the URL,
 * `navigate(path)` is a write that does NOT reload. Plain `<a href>`
 * links continue to do full-page nav as a fallback.
 */

import { useEffect, useState } from 'react'

const PATHCHANGE_EVENT = 'tschepp:pathchange'

/** Reactive pathname. Re-renders consumers on browser back/forward AND
 *  on programmatic `navigate()` calls. */
export function usePathname(): string {
  const [path, setPath] = useState(() =>
    typeof window === 'undefined' ? '/' : window.location.pathname,
  )
  useEffect(() => {
    const onChange = () => setPath(window.location.pathname)
    window.addEventListener('popstate', onChange)
    window.addEventListener(PATHCHANGE_EVENT, onChange)
    return () => {
      window.removeEventListener('popstate', onChange)
      window.removeEventListener(PATHCHANGE_EVENT, onChange)
    }
  }, [])
  return path
}

/** Reactive search string (the `?...` part). Same notification channel as
 *  usePathname so callers re-render together. */
export function useSearch(): string {
  const [search, setSearch] = useState(() =>
    typeof window === 'undefined' ? '' : window.location.search,
  )
  useEffect(() => {
    const onChange = () => setSearch(window.location.search)
    window.addEventListener('popstate', onChange)
    window.addEventListener(PATHCHANGE_EVENT, onChange)
    return () => {
      window.removeEventListener('popstate', onChange)
      window.removeEventListener(PATHCHANGE_EVENT, onChange)
    }
  }, [])
  return search
}

/** Programmatic navigation without a full-page reload. Push the new URL
 *  onto the history stack, then signal subscribers to re-render.
 *  Pass `{ replace: true }` to use history.replaceState (no new entry). */
export function navigate(path: string, opts?: { replace?: boolean }): void {
  if (typeof window === 'undefined') return
  // Cross-origin or absolute http(s) URL → fall back to full-page nav so
  // we don't accidentally pretend a foreign URL is "this app".
  if (/^https?:\/\//i.test(path)) {
    window.location.href = path
    return
  }
  if (opts?.replace) {
    window.history.replaceState({}, '', path)
  } else {
    window.history.pushState({}, '', path)
  }
  window.dispatchEvent(new Event(PATHCHANGE_EVENT))
}
